/**
 * A small QR encoder - byte mode, error-correction level M, versions 1-16.
 *
 * Written rather than imported because a wallet that is meant to work
 * under a blockade should not need a CDN to show someone an address, and
 * because every third-party script in a wallet is a place a key can leak
 * from. Level M survives a fair amount of camera blur and print damage,
 * which is the realistic failure mode for an address on a phone screen or
 * a peer ticket on a printed slip.
 */

/* ---------------------------- GF(256) ------------------------------- */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d; // QR's primitive polynomial
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
})();

const mul = (a: number, b: number): number => (a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]]);

function rsGenerator(degree: number): Uint8Array {
  let poly = new Uint8Array([1]);
  for (let i = 0; i < degree; i++) {
    const next = new Uint8Array(poly.length + 1);
    for (let j = 0; j < poly.length; j++) {
      next[j] ^= poly[j];
      next[j + 1] ^= mul(poly[j], EXP[i]);
    }
    poly = next;
  }
  return poly;
}

function rsEncode(data: Uint8Array, ecLen: number): Uint8Array {
  const gen = rsGenerator(ecLen);
  const res = new Uint8Array(ecLen);
  for (const byte of data) {
    const factor = byte ^ res[0];
    res.copyWithin(0, 1);
    res[ecLen - 1] = 0;
    if (factor !== 0) for (let i = 0; i < ecLen; i++) res[i] ^= mul(gen[i + 1], factor);
  }
  return res;
}

/* ------------------------- version tables --------------------------- */

/** Per version (1-16) at level M: [ecPerBlock, group1Blocks, group2Blocks]. */
const EC_M: [number, number, number][] = [
  [10, 1, 0], [16, 1, 0], [26, 1, 0], [18, 2, 0], [24, 2, 0], [16, 4, 0], [18, 4, 0], [22, 2, 2],
  [22, 3, 2], [26, 4, 1], [30, 1, 4], [22, 6, 2], [22, 8, 1], [24, 4, 5], [24, 5, 5], [28, 7, 3],
];

const ALIGN_POS: number[][] = [
  [], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50],
  [6, 30, 54], [6, 32, 58], [6, 34, 62], [6, 26, 46, 66], [6, 26, 48, 70], [6, 26, 50, 74],
];

/** Total codewords per version, from the specification's table. */
const TOTAL_CODEWORDS = [26, 44, 70, 100, 134, 172, 196, 242, 292, 346, 404, 466, 532, 581, 655, 733];

/* --------------------------- bit writer ----------------------------- */

class BitBuffer {
  bits: number[] = [];
  put(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i--) this.bits.push((value >>> i) & 1);
  }
  get length(): number {
    return this.bits.length;
  }
  toBytes(): Uint8Array {
    const out = new Uint8Array(Math.ceil(this.bits.length / 8));
    this.bits.forEach((b, i) => {
      if (b) out[i >> 3] |= 0x80 >> (i & 7);
    });
    return out;
  }
}

/* ---------------------------- encoding ------------------------------ */

export interface QrCode {
  size: number;
  /** Row-major booleans; true is a dark module. */
  modules: boolean[][];
  version: number;
}

export function encodeQr(text: string): QrCode {
  const data = new TextEncoder().encode(text);

  let version = 0;
  for (let v = 1; v <= 16; v++) {
    const [ecPer, g1, g2] = EC_M[v - 1];
    const capacityBytes = TOTAL_CODEWORDS[v - 1] - ecPer * (g1 + g2);
    const lenBits = v < 10 ? 8 : 16;
    if (4 + lenBits + data.length * 8 <= capacityBytes * 8) {
      version = v;
      break;
    }
  }
  if (!version) throw new Error('too much data for a level-M QR up to version 16');

  const [ecPerBlock, g1Blocks, g2Blocks] = EC_M[version - 1];
  const blocks = g1Blocks + g2Blocks;
  const dataCodewords = TOTAL_CODEWORDS[version - 1] - ecPerBlock * blocks;
  const g1Size = Math.floor(dataCodewords / blocks);
  const g2Size = g1Size + 1;

  const bb = new BitBuffer();
  bb.put(0b0100, 4); // byte mode
  bb.put(data.length, version < 10 ? 8 : 16);
  for (const b of data) bb.put(b, 8);
  const capacityBits = dataCodewords * 8;
  bb.put(0, Math.min(4, capacityBits - bb.length)); // terminator
  while (bb.length % 8 !== 0) bb.put(0, 1);
  const padBytes = [0xec, 0x11];
  let pad = 0;
  while (bb.length < capacityBits) bb.put(padBytes[pad++ % 2], 8);

  const all = bb.toBytes();
  const dataBlocks: Uint8Array[] = [];
  const ecBlocks: Uint8Array[] = [];
  let offset = 0;
  for (let i = 0; i < blocks; i++) {
    const size = i < g1Blocks ? g1Size : g2Size;
    const block = all.slice(offset, offset + size);
    offset += size;
    dataBlocks.push(block);
    ecBlocks.push(rsEncode(block, ecPerBlock));
  }

  // Interleave, as the spec requires for burst-error tolerance.
  const final: number[] = [];
  for (let i = 0; i < g2Size; i++) for (const b of dataBlocks) if (i < b.length) final.push(b[i]);
  for (let i = 0; i < ecPerBlock; i++) for (const b of ecBlocks) final.push(b[i]);

  return placeModules(version, new Uint8Array(final));
}

/* ---------------------------- placement ----------------------------- */

function placeModules(version: number, codewords: Uint8Array): QrCode {
  const size = 17 + 4 * version;
  const m: (boolean | null)[][] = Array.from({ length: size }, () => Array<boolean | null>(size).fill(null));

  const setFinder = (r: number, c: number): void => {
    for (let dr = -1; dr <= 7; dr++) {
      for (let dc = -1; dc <= 7; dc++) {
        const rr = r + dr, cc = c + dc;
        if (rr < 0 || cc < 0 || rr >= size || cc >= size) continue;
        const inRing = dr >= 0 && dr <= 6 && dc >= 0 && dc <= 6 &&
          (dr === 0 || dr === 6 || dc === 0 || dc === 6 || (dr >= 2 && dr <= 4 && dc >= 2 && dc <= 4));
        m[rr][cc] = inRing;
      }
    }
  };
  setFinder(0, 0);
  setFinder(0, size - 7);
  setFinder(size - 7, 0);

  for (let i = 8; i < size - 8; i++) {
    const dark = i % 2 === 0;
    m[6][i] = dark;
    m[i][6] = dark;
  }

  const pos = ALIGN_POS[version - 1];
  const first = pos[0];
  const last = pos[pos.length - 1];
  for (const r of pos) {
    for (const c of pos) {
      // The three finder corners are the only exclusions. Positions that
      // merely cross the timing pattern are drawn, and agree with it.
      if ((r === first && c === first) || (r === first && c === last) || (r === last && c === first)) continue;
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) {
          m[r + dr][c + dc] = Math.max(Math.abs(dr), Math.abs(dc)) !== 1;
        }
      }
    }
  }

  m[size - 8][8] = true; // dark module

  // Reserve format areas so data placement skips them.
  const reserve = (r: number, c: number): void => {
    if (m[r][c] === null) m[r][c] = false;
  };
  for (let i = 0; i < 9; i++) {
    reserve(8, i);
    reserve(i, 8);
  }
  for (let i = 0; i < 8; i++) {
    reserve(8, size - 1 - i);
    reserve(size - 1 - i, 8);
  }
  if (version >= 7) {
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 3; j++) {
        reserve(i, size - 11 + j);
        reserve(size - 11 + j, i);
      }
    }
  }

  const isFunction = m.map((row) => row.map((v) => v !== null));

  // Zig-zag placement, right to left, skipping the timing column.
  let bitIndex = 0;
  let upward = true;
  for (let col = size - 1; col >= 1; col -= 2) {
    if (col === 6) col--;
    for (let i = 0; i < size; i++) {
      const row = upward ? size - 1 - i : i;
      for (let k = 0; k < 2; k++) {
        const c = col - k;
        if (isFunction[row][c]) continue;
        const byte = codewords[bitIndex >> 3];
        const bit = byte !== undefined ? (byte >> (7 - (bitIndex & 7))) & 1 : 0;
        m[row][c] = bit === 1;
        bitIndex++;
      }
    }
    upward = !upward;
  }

  // Try all eight masks and keep the one the spec's penalty rules like.
  let best: { mask: number; grid: boolean[][]; penalty: number } | null = null;
  for (let mask = 0; mask < 8; mask++) {
    const grid = m.map((row, r) => row.map((v, c) => {
      const on = v === true;
      if (isFunction[r][c]) return on;
      return on !== maskAt(mask, r, c);
    }));
    applyFormat(grid, size, mask);
    if (version >= 7) applyVersion(grid, size, version);
    const penalty = scorePenalty(grid, size);
    if (!best || penalty < best.penalty) best = { mask, grid, penalty };
  }

  return { size, modules: best!.grid, version };
}

function maskAt(mask: number, r: number, c: number): boolean {
  switch (mask) {
    case 0: return (r + c) % 2 === 0;
    case 1: return r % 2 === 0;
    case 2: return c % 3 === 0;
    case 3: return (r + c) % 3 === 0;
    case 4: return (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0;
    case 5: return ((r * c) % 2) + ((r * c) % 3) === 0;
    case 6: return (((r * c) % 2) + ((r * c) % 3)) % 2 === 0;
    default: return (((r + c) % 2) + ((r * c) % 3)) % 2 === 0;
  }
}

function applyFormat(grid: boolean[][], size: number, mask: number): void {
  const ecBits = 0b00; // level M
  let value = (ecBits << 3) | mask;
  let rem = value << 10;
  for (let i = 4; i >= 0; i--) if (rem & (1 << (i + 10))) rem ^= 0b10100110111 << i;
  const bits = ((value << 10) | rem) ^ 0b101010000010010;

  const bit = (i: number) => ((bits >> i) & 1) === 1;
  // First copy: down column 8, then left along row 8.
  for (let i = 0; i <= 5; i++) grid[i][8] = bit(i);
  grid[7][8] = bit(6);
  grid[8][8] = bit(7);
  grid[8][7] = bit(8);
  for (let i = 9; i <= 14; i++) grid[8][14 - i] = bit(i);

  // Second copy: right along row 8, then up column 8.
  for (let i = 0; i <= 7; i++) grid[8][size - 1 - i] = bit(i);
  for (let i = 8; i <= 14; i++) grid[size - 15 + i][8] = bit(i);
  grid[size - 8][8] = true; // always dark
}

function applyVersion(grid: boolean[][], size: number, version: number): void {
  let rem = version << 12;
  for (let i = 5; i >= 0; i--) if (rem & (1 << (i + 12))) rem ^= 0b1111100100101 << i;
  const bits = (version << 12) | rem;
  for (let i = 0; i < 18; i++) {
    const bit = ((bits >> i) & 1) === 1;
    const r = Math.floor(i / 3);
    const c = size - 11 + (i % 3);
    grid[r][c] = bit;
    grid[c][r] = bit;
  }
}

function scorePenalty(grid: boolean[][], size: number): number {
  let penalty = 0;
  // Rule 1: runs of five or more.
  for (let i = 0; i < size; i++) {
    for (const line of [grid[i], grid.map((row) => row[i])]) {
      let run = 1;
      for (let j = 1; j < size; j++) {
        if (line[j] === line[j - 1]) run++;
        else {
          if (run >= 5) penalty += run - 2;
          run = 1;
        }
      }
      if (run >= 5) penalty += run - 2;
    }
  }
  // Rule 2: 2x2 blocks of one colour.
  for (let r = 0; r < size - 1; r++) {
    for (let c = 0; c < size - 1; c++) {
      const v = grid[r][c];
      if (v === grid[r][c + 1] && v === grid[r + 1][c] && v === grid[r + 1][c + 1]) penalty += 3;
    }
  }
  // Rule 3: finder-like patterns.
  const pat = [true, false, true, true, true, false, true, false, false, false, false];
  const rpat = [...pat].reverse();
  const matches = (line: boolean[], at: number, p: boolean[]) => p.every((v, k) => line[at + k] === v);
  for (let i = 0; i < size; i++) {
    const row = grid[i];
    const col = grid.map((r) => r[i]);
    for (let j = 0; j + 11 <= size; j++) {
      if (matches(row, j, pat) || matches(row, j, rpat)) penalty += 40;
      if (matches(col, j, pat) || matches(col, j, rpat)) penalty += 40;
    }
  }
  // Rule 4: overall balance.
  let dark = 0;
  for (const row of grid) for (const v of row) if (v) dark++;
  const pct = (dark * 100) / (size * size);
  penalty += Math.floor(Math.abs(pct - 50) / 5) * 10;
  return penalty;
}

/* ----------------------------- rendering ---------------------------- */

/** Inline SVG. No canvas, no images, no network. */
export function qrToSvg(text: string, opts: { margin?: number; dark?: string; light?: string } = {}): string {
  const { size, modules } = encodeQr(text);
  const margin = opts.margin ?? 2;
  const dim = size + margin * 2;
  const dark = opts.dark ?? '#000';
  const light = opts.light ?? 'none';

  let path = '';
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (modules[r][c]) path += `M${c + margin} ${r + margin}h1v1h-1z`;
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${dim} ${dim}" shape-rendering="crispEdges" role="img" aria-label="QR code">` +
    (light === 'none' ? '' : `<rect width="${dim}" height="${dim}" fill="${light}"/>`) +
    `<path d="${path}" fill="${dark}"/></svg>`;
}
